import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Lightbulb, MapPin, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toEs } from "@/lib/i18n";
import { GrantSpotlight } from "@/components/GrantSpotlight";

export function JourneyMap({ lang = "en" }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/journey/me", { params: { lang } }).then((r) => setD(r.data)).catch(() => {}); }, [lang]);
  if (!d) return null;
  const es = lang === "es";
  const cur = d.stages.find((s) => s.key === d.current);
  const idx = d.stages.findIndex((s) => s.key === d.current);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="journey-map" data-no-translate>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold text-slate-900">{es ? "Tu camino hacia la subvención" : "Your journey to grant funding"}</h2>
        <div className="flex items-center gap-2" data-testid="journey-trophies">
          {(d.milestones || []).map((m) => (
            <span key={m.key} title={m.done ? `${m.label}${m.at ? ` · ${new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(m.at) ? m.at : `${m.at}Z`).toLocaleDateString()}` : ""}` : `${m.label} — ${es ? "aún no" : "not yet"}`}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.done ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-slate-50 text-slate-500 border border-slate-200"}`} data-testid={`journey-trophy-${m.key}${m.done ? "-earned" : ""}`}>
              <Trophy className="h-3 w-3" />{m.label}
            </span>
          ))}
        </div>
        <span className="text-xs text-slate-500" data-testid="journey-map-progress">{es ? `Etapa ${idx + 1} de 8` : `Stage ${idx + 1} of 8`}</span>
      </div>
      <ol className="mt-6 grid grid-cols-4 md:grid-cols-8 gap-y-6">
        {d.stages.map((s, i) => {
          const here = s.key === d.current;
          return (
            <li key={s.key} className="relative flex flex-col items-center text-center px-1" data-testid={`journey-stage-${s.key}`}>
              {i > 0 && <span className={`absolute top-4 right-1/2 w-full h-0.5 -z-0 ${s.done || here ? "bg-emerald-500" : "bg-slate-200"}`} />}
              <Link to={s.page} className={`relative z-10 h-8 w-8 rounded-full grid place-items-center text-xs font-bold transition-transform hover:scale-110 ${s.done ? "bg-emerald-600 text-white" : here ? "bg-amber-400 text-slate-900 ring-4 ring-amber-100" : "bg-slate-100 text-slate-400"}`}
                data-testid={`journey-stage-dot-${s.key}`}>{s.done ? <Check className="h-4 w-4" /> : i + 1}</Link>
              {s.done && ["licensing", "review", "awarded"].includes(s.key) && <Trophy className="absolute -top-3 left-1/2 ml-2 h-4 w-4 text-amber-500 fill-amber-300 z-20" data-testid={`journey-stage-trophy-${s.key}`} />}
              <span className={`mt-2 text-[11px] leading-tight ${here ? "font-semibold text-slate-900" : "text-slate-500"}`}>{s.title}</span>
              {here && <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-0.5" data-testid="journey-you-are-here"><MapPin className="h-3 w-3" />{es ? "Estás aquí" : "You are here"}</span>}
            </li>
          );
        })}
      </ol>
      {cur && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{es ? "Tu siguiente paso" : "Your next step"}</div>
            <p className="text-sm text-slate-800 mt-1" data-testid="journey-next-step">{cur.next}</p>
            <Link to={cur.page}><Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="journey-next-button">{es ? "Ir ahora" : "Go there now"}<ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="journey-industry-tip">
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" />{d.industry ? (es ? `Consejo para ${toEs(d.industry) || d.industry}` : `${d.industry} tip`) : (es ? "Consejo" : "Tip")}</div>
            <p className="text-sm text-slate-800 mt-1">{cur.tip || (es ? "Elige tu industria en el perfil de la empresa para recibir consejos de financiamiento específicos." : "Choose your industry in your company profile to get funding tips made for your field.")}</p>
          </div>
        </div>
      )}
      <GrantSpotlight lang={lang} />
    </div>
  );
}
